// GOOGLE TAG MANAGER MIXIN for e-commerce
import {canViewTestCode} from '@/js/global/config.js';

import {
   MENUS_PRODUCT_TYPE,
   DISHES_PRODUCT_TYPE,
   DRINKS_PRODUCT_TYPE,
   ACCESSORIES_PRODUCT_TYPE,
   SHIPPING_ADJUSTER_TYPE,
   DISCOUNT_ADJUSTER_TYPE,
   BANK_TRANSFER_GATEWAY_ID,
   CARD_GATEWAY_ID,
   INVOICE_GATEWAY_ID,
   PAYPAL_GATEWAY_ID,
   SOFORT_GATEWAY_ID,
} from '@/js/funnel/_config/commerce';

import {
   isGtmEnabled,
   pushCommerceEventToDataLayer,
   gtmItem,
   IMPRESSION_EVENT_VIEW_ITEM_LIST,
   ACTION_EVENT_SELECT_ITEM,
   IMPRESSION_EVENT_VIEW_ITEM_DETAILS,
   ACTION_EVENT_ADD_TO_CART,
   ACTION_EVENT_REMOVE_FROM_CART,
   ACTION_EVENT_BEGIN_CHECKOUT,
   ACTION_EVENT_ADD_SHIPPING_INFO,
   ACTION_EVENT_ADD_PAYMENT_INFO,
} from '@/js/global/helpers/gtm';


/**
 * Methods for firing off GTM ecommerce events on the "Menus Listing" page.
 *
 * @type {{methods: {gtmViewListOfMenus(): void, gtmSelectMenu(): void}}}
 */
export const GtmEventsMenusListingMixin = {
   data() {
      return {
         gtmItemsOfMenus: {},
      }
   },
   mounted() {
      let impressionsList = [];

      // loop through the menu refs, create a 'gtmItem' for it, and store it in local data for
      // reporting GTM events
      for (const vueRef in this.$refs) {
         try {
            const menu = JSON.parse(this.$refs[vueRef].dataset.gtm);
            const menuGtmItem = gtmItem(menu.item, menu.category, menu.list);
            this.gtmItemsOfMenus[vueRef] = {...menuGtmItem};
            impressionsList.push(menuGtmItem);
         }
         catch (e) {
            if(window.Sentry) {
               Sentry.captureException(e);
            }
            console.error(e);
         }
      }

      // report an event that the customer has seen the list of menus
      if (this.isGtmEnabled) {
         pushCommerceEventToDataLayer(IMPRESSION_EVENT_VIEW_ITEM_LIST, impressionsList);
      }
   },
   methods: {
      /**
       * When the customer clicks on a menu card on the "Menus Listing" page.
       *
       * @param menuVueRef {string}
       * @return {boolean}
       */
      gtmSelectMenu(menuVueRef) {
         if (! this.isGtmEnabled) {
            return false;
         }

         try {
            let gtmItem = this.gtmItemsOfMenus[menuVueRef] ?? null;
            if (gtmItem !== null) {
               pushCommerceEventToDataLayer(ACTION_EVENT_SELECT_ITEM, [gtmItem]);
               return true;
            }
         }
         catch (e) {
            if(window.Sentry) {
               Sentry.captureException(e);
            }
            console.error(e);
         }

         return false;
      },
   },
};


/**
 * Methods for firing off GTM events on a "Menu Details" page (aka the "Selection" page).
 *
 * @type {{methods: {gtmAddItemToCart(*): void, gtmViewMenuDetails(): void, gtmViewItemDetails():
 *    void, gtmViewItemsInCurrentSection(): void, gtmRemoveItemFromCart(*): void}}}
 */
export const GtmEventsMenuItemsSelectionMixin = {
   methods: {
      /**
       * When a customer lands on a menu's details page to select browse its items.
       */
      gtmViewMenuDetails() {
         if (this.isGtmEnabled) {
            pushCommerceEventToDataLayer(
               IMPRESSION_EVENT_VIEW_ITEM_DETAILS,
               [this.gtmItemObjectForMenu()],
               {
                  currency: window.mymy.request.currency,
                  value: (window.mymy.menu.price.gross / 100),
               }
            );
         }
      },

      /**
       * When a menu's items in the current section become visible to the customer.
       */
      gtmViewItemsInCurrentSection() {
         if (! this.isGtmEnabled) {
            return;
         }

         let items = [];
         for (const MenuItemControls of this.currentSection.allMenuItems) {
            items.push(MenuItemControls.gtmItem());
         }
         if (items.length) {
            pushCommerceEventToDataLayer(IMPRESSION_EVENT_VIEW_ITEM_LIST, items);
         }
      },

      /**
       * When a customer clicks on an item to view more details (currently displayed in a modal).
       *
       * @param MenuItemControls {<MenuItemControls>}
       * @see ./src/js/funnel/_components/menu/MenuItemControls.vue
       */
      gtmViewItemDetails(MenuItemControls) {
         if (this.isGtmEnabled) {
            pushCommerceEventToDataLayer(
               IMPRESSION_EVENT_VIEW_ITEM_DETAILS,
               [MenuItemControls.gtmItem()],
               {currency: window.mymy.request.currency, value: MenuItemControls.priceAsFloat}
            );
         }
      },

      /**
       * When a customer adds an item to their cart (including a package menu).
       *
       * Pass in a 'gtmItem' object instead of a MenuItemControls component, since a package
       * menu could be added/removed from a cart as a line item, and it has no vue component.
       *
       * @param gtmItem {object}
       * @param price {number}
       */
      gtmAddItemToCart(gtmItem, price) {
         if (this.isGtmEnabled) {
            pushCommerceEventToDataLayer(
               ACTION_EVENT_ADD_TO_CART,
               [gtmItem],
               {currency: window.mymy.request.currency, value: price}
            );
         }
      },

      /**
       * When a customer removes an item from their cart.
       *
       * Pass in a 'gtmItem' object instead of a MenuItemControls component, since a package
       * menu could be added/removed from a cart as a line item, and it has no vue component.
       *
       * @param gtmItem {object}
       * @param price {number}
       */
      gtmRemoveItemFromCart(gtmItem, price) {
         if (this.isGtmEnabled) {
            pushCommerceEventToDataLayer(
               ACTION_EVENT_REMOVE_FROM_CART,
               [gtmItem],
               {currency: window.mymy.request.currency, value: price}
            );
         }
      },
   },
};


/**
 * Methods for firing off GTM events on all of the checkout pages ("Delivery" and "Overview").
 *
 * @type {{methods: {gtmBeginCheckout(): void, gtmAddPaymentInfo(): void, gtmAddShippingInfo():
 *    void}}}
 */
export const GtmEventsCheckoutMixin = {
   methods: {
      /**
       * When the customer lands on the "Delivery" page of the funnel to start the checkout process.
       */
      gtmBeginCheckout() {
         if (this.isGtmEnabled) {
            pushCommerceEventToDataLayer(
               ACTION_EVENT_BEGIN_CHECKOUT,
               this.gtmItemsFromCart(),
               {
                  currency: window.mymy.request.currency,
                  value: (this.$store.getters.summaryTotalGross / 100),
                  coupon: this.cart.couponCode ?? '',
               }
            );
         }
      },

      /**
       * When the customer submits their shipping/delivery information.
       */
      gtmAddShippingInfo() {
         if (this.isGtmEnabled) {
            pushCommerceEventToDataLayer(
               ACTION_EVENT_ADD_SHIPPING_INFO,
               this.gtmItemsFromCart(),
               {
                  currency: window.mymy.request.currency,
                  value: (this.$store.getters.summaryTotalGross / 100),
                  coupon: this.cart.couponCode ?? '',
                  shipping_tier: this.cart.shippingMethod.name ?? '',
               }
            );
         }
      },

      /**
       * When the customer submits their payment details (card, paypal, etc).
       */
      gtmAddPaymentInfo() {
         if (! this.isGtmEnabled) {
            return;
         }

         let gatewayName = '';
         switch (this.cart.gatewayId) {
            case CARD_GATEWAY_ID:
               gatewayName = 'Credit Card';
               break;
            case PAYPAL_GATEWAY_ID:
               gatewayName = 'PayPal';
               break;
            case BANK_TRANSFER_GATEWAY_ID:
               gatewayName = 'Bank Transfer';
               break;
            case INVOICE_GATEWAY_ID:
               gatewayName = 'Invoice';
               break;
            case SOFORT_GATEWAY_ID:
               gatewayName = 'SOFORT';
               break;
         }

         pushCommerceEventToDataLayer(
            ACTION_EVENT_ADD_PAYMENT_INFO,
            this.gtmItemsFromCart(),
            {
               currency: window.mymy.request.currency,
               value: (this.$store.getters.summaryTotalGross / 100),
               coupon: this.cart.couponCode ?? '',
               payment_type: gatewayName,
            }
         );
      },
   },
};


/**
 * Helper methods available for all pages in the funnel.
 *
 * @type {{methods: {gtmItemObjectForMenu(Object=): *}}}
 */
export const GtmEcommerceHelperMixin = {
   mounted() {
      if (canViewTestCode) {
         console.log('Google Tag Manager is enabled:', this.isGtmEnabled);
      }
   },
   computed: {
      isGtmEnabled: () => isGtmEnabled(),
   },
   methods: {
      /**
       * Use this method to get an array of all the line items in the cart, which can be used
       * for the 'items' array in a GTM event.
       *
       * @return {array}
       */
      gtmItemsFromCart() {
         let items = [];

         // first add the (package) menu
         if (! window.mymy.menu.isALaCarte && this.lineItems[MENUS_PRODUCT_TYPE]) {
            // add the package menu that's a line item
            items.push(this.gtmItemObjectForMenu());

            // add all the menu's selected course dishes
            for (const id in this.summary.courseItems) {
               let MenuItemControls = this.getMenuItemComponent(id);
               if (MenuItemControls) {
                  items.push(MenuItemControls.gtmItem());
               }
            }
         }

         // now do the rest of the line items in the specific order of their product type
         for (const productTypeHandle of [DISHES_PRODUCT_TYPE, DRINKS_PRODUCT_TYPE, ACCESSORIES_PRODUCT_TYPE]) {
            for (const id in this.lineItems[productTypeHandle]) {
               let MenuItemControls = this.getMenuItemComponent(id);
               if (MenuItemControls) {
                  items.push(MenuItemControls.gtmItem());
               }
            }
         }

         // lastly, add the adjustments (shipping & discounts)
         for (const handle of [SHIPPING_ADJUSTER_TYPE, DISCOUNT_ADJUSTER_TYPE]) {
            for (const adjustment of this.summary.adjustments[handle]) {
               let gtmItem = this.gtmItemObjectForAdjustment(adjustment);
               if (gtmItem) {
                  items.push(gtmItem);
               }
            }
         }

         return items;
      },

      /**
       * Get a formatted 'item' object for the menu the customer is currently viewing, which can
       * be used in a GTM event's 'items' array.
       *
       * For all other items, their MenuItemControls component will provide its own method to
       * construct this object. However, this method is needed for package menus, since they do
       * not have their own MenuItemControls component.
       *
       * @param custom {object}
       * @return {object}
       * @see ./src/js/funnel/_components/menu/MenuItemControls::gtmItem
       * @see gtmItemObjectForAdjustment
       * @see https://developers.google.com/analytics/devguides/collection/ga4/item-scoped-ecommerce#add_an_item-scoped_custom_parameter
       * @see https://www.simoahava.com/analytics/google-analytics-4-ecommerce-guide-google-tag-manager/#product-data
       */
      gtmItemObjectForMenu(custom = {}) {

         // figure out the this menu's quantity
         let quantity = window.mymy.menu.quantity.min;
         if (window.mymy.menu.isALaCarte) {
            try {
               quantity = this.$store.state.cart.numberOfPeople;
            }
            catch (e) {
               if(window.Sentry) {
                  Sentry.captureException(e);
               }
               console.error(e);
            }
         }
         else {
            try {
               quantity = this.$store.state.summary.lineItems.menus.quantity;
            }
            catch (e) {
               if(window.Sentry) {
                  Sentry.captureException(e);
               }
               console.error(e);
            }
         }

         return gtmItem(
            // item
            {
               id: custom.id ?? window.mymy.menu.sku,
               locationId: window.mymy.request.currentSite.handle,
               name: custom.name ?? window.mymy.menu.title,
               price: custom.price ?? (window.mymy.menu.price.gross / 100),
               quantity: custom.quantity ?? quantity,
               variant: custom.variant ?? window.mymy.menu.title,
            },
            // category
            [
               custom.category ?? window.mymy.menu.category,
            ],
            // list
            {
               index: custom.index ?? null,
               name: custom.listName ?? window.mymy.menu.category,
               id: custom.listId ?? null,
            },
            // custom
            {
               is_a_la_carte: custom.isALaCarte ?? window.mymy.menu.isALaCarte,
            }
         );
      },

      /**
       * Get a formatted 'item' object for an adjustment (discount or shipping) in the order
       * summary, which can be used in a GTM event's 'items' array.
       *
       * @param adjustment
       * @param custom
       * @return {null|object}
       * @see ./src/js/funnel/_components/menu/MenuItemControls::gtmItem
       * @see gtmItemObjectForMenu
       * @see https://developers.google.com/analytics/devguides/collection/ga4/item-scoped-ecommerce#add_an_item-scoped_custom_parameter
       * @see https://www.simoahava.com/analytics/google-analytics-4-ecommerce-guide-google-tag-manager/#product-data
       */
      gtmItemObjectForAdjustment(adjustment, custom = {}) {
         if (! adjustment.type) {
            return null;
         }

         // the adjustment object must have a 'type' that's one of these
         let adjustmentTypeName, categoryName = '';
         switch (adjustment.type) {
            case SHIPPING_ADJUSTER_TYPE:
               adjustmentTypeName = 'Shipping';
               categoryName = 'Shipping Fee'
               break;
            case DISCOUNT_ADJUSTER_TYPE:
               adjustmentTypeName = 'Discount';
               categoryName = 'Discounts';
               break;
            default:
               return null;
         }

         // deduce the price
         let price = 0;
         try {
            price = adjustment.price.gross;
         }
         catch (e) {
            if(window.Sentry) {
               Sentry.captureException(e);
            }
            console.error(e);
         }
         if (custom.price ?? null) {
            price = custom.price;
         }

         return gtmItem(
            // item
            {
               id: custom.id ?? adjustment.slug ?? null,
               locationId: window.mymy.request.currentSite.handle,
               name: custom.name ?? adjustment.title ?? null,
               price: (price / 100),
               quantity: custom.quantity ?? adjustment.quantity ?? null,
               variant: custom.type ?? adjustment.type,
            },
            // category
            [
               custom.category ?? categoryName,
            ],
            // list
            {
               index: custom.index ?? null,
               name: custom.listName ?? adjustmentTypeName,
               id: custom.listId ?? adjustment.type,
            },
         );
      },
   },
};